import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { motion } from 'framer-motion';
import styles from './FileUploadModal.module.scss';
import ModalOverlay from '../../../components/UI/ModalOverlay/ModalOverlay.tsx';
import { FileItem, FileTag } from '../../../types/projectDocumentation';
import { X, Search, UploadIcon } from 'lucide-react';
import FileIcon from '../../../assets/iconsTsx/FileIcon.tsx';
import PdfFileIcon from '../../../assets/iconsTsx/PdfFileIcon.tsx';
import GoogleDriveIcon from '../../../assets/iconsTsx/GoogleDriveIcon.tsx';
import CheckIcon from '../../../assets/iconsTsx/CheckIcon.tsx';
import { openMainModal } from '../../../store/actions/profileActions';
import { 
  DEFAULT_FILE_CONFIG, 
  getFileConfig,
  validateFileSize, 
  formatFileSize 
} from '../../../constants/fileConfig.ts';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: FileItem[];
  tags: FileTag[];
  onItemsChange: (items: FileItem[]) => void;
  loading?: boolean;
  openMainModal: (config: any) => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  items,
  tags,
  onItemsChange,
  loading = false,
  openMainModal
}) => {
  const [localItems, setLocalItems] = useState<FileItem[]>(items);
  const [uploadMode, setUploadMode] = useState<'file' | 'link'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [linkInput, setLinkInput] = useState('');
  const [confirmedLink, setConfirmedLink] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [tagSearch, setTagSearch] = useState('');
  const [linkError, setLinkError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalItemsRef = useRef<FileItem[]>([]);

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      originalItemsRef.current = [...items];
      setLocalItems(items);
    }
  }, [isOpen, items]);

  const hasChanges = () => {
    if (localItems.length !== originalItemsRef.current.length) {
      return true;
    }
    
    const originalIds = new Set(originalItemsRef.current.map(item => item.id));
    const localIds = new Set(localItems.map(item => item.id));
    
    if (originalIds.size !== localIds.size) {
      return true;
    }
    
    for (const id of originalIds) {
      if (!localIds.has(id)) {
        return true;
      }
    }
    
    for (const id of localIds) {
      if (!originalIds.has(id)) {
        return true;
      }
    }
    
    return false;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const selectedTagObj = tags.find(tag => tag.id === selectedTag);
    const tagName = selectedTagObj?.name || '';
    
    const validation = validateFileSize(file, tagName);
    
    if (!validation.valid) {
      const modalConfig: any = {
        isSuccess: true,
        title: validation.suggestLink ? 'Archivo muy pesado' : 'Archivo no permitido',
        body: validation.error,
        iconType: 'warning',
        noActionNeeded: true
      };
      
      if (validation.suggestLink) {
        modalConfig.body += '\n\nTe recomendamos usar la opción de "Link" para compartir el archivo desde tu Google Drive, Dropbox u otro servicio de almacenamiento.';
      }
      
      openMainModal(modalConfig);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    setSelectedFile(file);
  };

  const handleConfirmLink = () => {
    if (!linkInput.trim()) {
      setLinkError('Por favor ingresa una URL');
      return;
    }
    
    try {
      const url = new URL(linkInput);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        setLinkError('La URL debe empezar con http:// o https://');
        return;
      }
    } catch (e) {
      setLinkError('Por favor ingresa una URL válida');
      return;
    }

    setConfirmedLink(linkInput);
    setLinkInput('');
    setLinkError('');
  };

  const handleAddItem = () => {
    if (uploadMode === 'file' && !selectedFile) return;
    if (uploadMode === 'link' && !confirmedLink) return;

    if (!selectedTag) {
      openMainModal({
        isSuccess: false,
        title: 'Etiqueta requerida',
        body: 'Por favor selecciona una etiqueta para el documento',
        iconType: 'warning',
        noActionNeeded: true
      });
      return;
    }

    if (uploadMode === 'file' && selectedFile) {
      const duplicateFile = localItems.find(
        item => item.type === 'file' && item.name === selectedFile.name
      );
      if (duplicateFile) {
        openMainModal({
          isSuccess: true,
          title: 'Archivo duplicado',
          body: `El archivo "${selectedFile.name}" ya fue agregado. Por favor selecciona un archivo diferente.`,
          iconType: 'warning',
          noActionNeeded: true
        });
        return;
      }
    }

    if (uploadMode === 'link' && confirmedLink) {
      const duplicateLink = localItems.find(
        item => item.type === 'link' && item.link === confirmedLink
      );
      if (duplicateLink) {
        openMainModal({
          isSuccess: false,
          title: 'Link duplicado',
          body: `Este link ya fue agregado. Por favor ingresa un link diferente.`,
          iconType: 'warning',
          noActionNeeded: true
        });
        return;
      }
    }

    const newItem: FileItem = uploadMode === 'file' && selectedFile ? {
      id: `${Date.now()}-${Math.random()}`,
      type: 'file',
      file: selectedFile,
      preview: selectedFile.type.startsWith('image/') ? URL.createObjectURL(selectedFile) : undefined,
      tagId: selectedTag,
      name: selectedFile.name
    } : {
      id: `${Date.now()}-${Math.random()}`,
      type: 'link',
      link: confirmedLink,
      tagId: selectedTag,
      name: confirmedLink
    };

    setLocalItems([...localItems, newItem]);
    
    setSelectedFile(null);
    setLinkInput('');
    setConfirmedLink('');
    setSelectedTag('');
    setTagSearch('');
    setLinkError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveItem = (itemId: string) => {
    setLocalItems(localItems.filter(item => item.id !== itemId));
  };

  const handleSave = () => {
    onItemsChange(localItems);
    onClose();
  };

  const handleCancel = () => {
    setLocalItems(items);
    setSelectedFile(null);
    setLinkInput('');
    setConfirmedLink('');
    setSelectedTag('');
    setTagSearch('');
    setLinkError('');
    setUploadMode('file');
    onClose();
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1.0,
      y: 0,
      transition: { 
        delay: 0.1,
        duration: 0.3,
        ease: "easeOut"
      },
    },
  };

  return (
    <ModalOverlay 
      isOpen={isOpen}
      onClose={handleCancel}
    >
      <motion.div 
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
            
            {/* Header */}
            <div className={styles.header}>
              <h2 className={styles.title}>Agregar Documentación Adicional</h2>
              <button className={styles.closeButton} onClick={handleCancel} type="button">
                <X size={20} />
              </button>
            </div>

        {/* Body */}
        <div className={styles.body}>
          
          {/* Documento */}
          <div className={styles.section}>
            {/* <h3 className={styles.sectionTitle}>Documento</h3> */}
            <div className={styles.uploadRow}>
              {/* Toggle */}
              <div className={styles.toggleButtons}>
                <button
                  type="button"
                  className={`${styles.toggleButton} ${uploadMode === 'file' ? styles.active : ''}`}
                  onClick={() => setUploadMode('file')}
                >
                  Archivo
                </button>
                <button
                  type="button"
                  className={`${styles.toggleButton} ${uploadMode === 'link' ? styles.active : ''}`}
                  onClick={() => setUploadMode('link')}
                >
                  Link
                </button>
              </div>

              {/* Input/Preview */}
              {uploadMode === 'file' ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={selectedTag ? getFileConfig(tags.find(t => t.id === selectedTag)?.name || '').accept : DEFAULT_FILE_CONFIG.accept}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  {selectedFile ? (
                    <div className={styles.uploadedPreview}>
                      {selectedFile.type.startsWith('video/') ? (
                        <video 
                          src={URL.createObjectURL(selectedFile)} 
                          className={styles.previewThumb} 
                        />
                      ) : selectedFile.type.startsWith('image/') ? (
                        <img 
                          src={URL.createObjectURL(selectedFile)} 
                          alt={selectedFile.name} 
                          className={styles.previewThumb} 
                        />
                      ) : selectedFile.type === 'application/pdf' ? (
                        <PdfFileIcon className={styles.fileThumbIcon} />
                      ) : (
                        <FileIcon className={styles.fileThumbIcon} />
                      )}
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>{selectedFile.name}</span>
                        <span className={styles.fileSize}>{formatFileSize(selectedFile.size)}</span>
                      </div>
                      <button
                        type="button"
                        className={styles.clearButton}
                        onClick={() => setSelectedFile(null)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading}
                    >
                      <UploadIcon className={styles.icon} />
                     Seleccionar archivo
                    </button>
                  )}
                </>
              ) : confirmedLink ? (
                <div className={styles.uploadedPreview}>
                  <GoogleDriveIcon className={styles.linkIcon} />
                  <span className={styles.fileName}>{confirmedLink}</span>
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => setConfirmedLink('')}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className={styles.linkInputWrapper}>
                  <div className={styles.linkInputContainer}>
                    <input
                      type="url"
                      placeholder="https://drive.google.com/..."
                      value={linkInput}
                      onChange={(e) => {
                        setLinkInput(e.target.value);
                        setLinkError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleConfirmLink();
                        }
                      }}
                      className={`${styles.linkInput} ${linkError ? styles.linkInputError : ''}`}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className={styles.addLinkButton}
                      onClick={handleConfirmLink}
                      disabled={loading || !linkInput.trim()}
                    >
                      Agregar
                    </button>
                  </div>
                  {linkError && <span className={styles.linkErrorText}>{linkError}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Etiqueta */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Etiqueta</h3>
            
            <div className={styles.tagSearchContainer}>
              <Search size={16} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar etiqueta..."
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                className={styles.tagSearchInput}
              />
            </div>

            <div className={styles.tagsList}>
              {filteredTags.length === 0 ? (
                <p className={styles.noResults}>No se encontraron etiquetas</p>
              ) : (
                filteredTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className={`${styles.tagOption} ${selectedTag === tag.id ? styles.selected : ''}`}
                    onClick={() => {
                      setSelectedTag(tag.id);
                      setTagSearch(tag.name);
                    }}
                  >
                    {tag.name}
                    {selectedTag === tag.id && (
                      <div className={styles.checkmark}>
                        <CheckIcon className={styles.checkIcon} />
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Botón Agregar */}
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAddItem}
            disabled={
              loading ||
              (uploadMode === 'file' && !selectedFile) ||
              (uploadMode === 'link' && !confirmedLink) ||
              !selectedTag
            }
          >
            Agregar documento
          </button>

          {/* Lista de documentos agregados */}
          {localItems.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Documentos agregados ({localItems.length})</h3>
              <div className={styles.addedList}>
                {localItems.map(item => (
                  <div key={item.id} className={styles.addedItem}>
                    {item.type === 'link' ? (
                      <GoogleDriveIcon className={styles.itemIcon} />
                    ) : item.file?.type.startsWith('video/') ? (
                      <video src={item.preview} className={styles.itemThumb} />
                    ) : item.preview ? (
                      <img src={item.preview} alt={item.name} className={styles.itemThumb} />
                    ) : item.file?.type === 'application/pdf' ? (
                      <PdfFileIcon className={styles.itemIcon} />
                    ) : (
                      <FileIcon className={styles.itemIcon} />
                    )}
                    <div className={styles.itemInfo}>
                      <span className={styles.itemName}>{item.name}</span>
                      <div className={styles.itemMetadata}>
                        <span className={styles.itemTag}>
                          {tags.find(t => t.id === item.tagId)?.name}
                        </span>
                        {item.type === 'file' && item.file && (
                          <span className={styles.itemSize}>
                            {formatFileSize(item.file.size)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={handleCancel}>
            Cancelar
          </button>
          <button 
            type="button" 
            className={styles.saveButton} 
            onClick={handleSave} 
            disabled={loading || !hasChanges()}
          >
            Guardar
          </button>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default connect(null, {
  openMainModal
})(FileUploadModal);
